import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useLang } from "../i18n/LanguageContext";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { ArrowLeft, ArrowUpRight, Mail, Phone, Clock, Send } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const channelIcon = (label) => {
  const l = label.toLowerCase();
  if (l.startsWith("e-mail") || l.startsWith("email")) return Mail;
  if (l.startsWith("phone") || l.startsWith("telefon")) return Phone;
  return Clock;
};

const SupportPage = () => {
  const { t, lang } = useLang();
  const s = t.support;

  const [form, setForm] = useState({
    name: "",
    email: "",
    topic: "",
    reference: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [feedback, setFeedback] = useState("");

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
    if (!form.name.trim() || !emailOk || !form.topic || !form.message.trim()) {
      setStatus("error");
      setFeedback(s.form.validation);
      return;
    }
    setStatus("loading");
    setFeedback("");
    try {
      await axios.post(`${API}/support`, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        topic: form.topic,
        reference: form.reference.trim() || null,
        message: form.message.trim(),
        lang,
      });
      setStatus("success");
      setFeedback(s.form.success);
      setForm({ name: "", email: "", topic: "", reference: "", message: "" });
    } catch (err) {
      setStatus("error");
      setFeedback(s.form.error);
    }
  };

  return (
    <div data-testid="support-root" className="bg-[#f9f6f0] text-[#2c1e16] min-h-screen">
      <Header />

      {/* Hero */}
      <section
        data-testid="support-hero"
        className="pt-32 md:pt-40 pb-14 md:pb-20"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link
            to="/"
            data-testid="support-back-home"
            className="inline-flex items-center gap-2 font-mono-utility text-[10px] tracking-[0.25em] uppercase text-[#5b664f] hover:text-[#a64b2a] transition-colors mb-10"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
            {s.back_home}
          </Link>

          <div className="grid grid-cols-12 gap-6 md:gap-12 items-end">
            <div className="col-span-12 md:col-span-8">
              <div className="flex items-center gap-3 mb-6">
                <span className="editorial-divider" />
                <span
                  className="overline"
                  data-testid="support-overline"
                >
                  {s.overline}
                </span>
              </div>
              <h1
                data-testid="support-title"
                className="font-serif-display text-5xl sm:text-6xl md:text-7xl leading-[0.98] tracking-tight text-[#2c1e16] whitespace-pre-line"
              >
                {s.title}
              </h1>
            </div>
            <div className="col-span-12 md:col-span-4">
              <p className="text-base md:text-lg leading-relaxed text-[#2c1e16]/75">
                {s.lead}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Channels */}
      <section
        data-testid="support-channels"
        className="py-14 md:py-20 bg-[#f2ebe1]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-10">
            <span className="editorial-divider" />
            <span className="overline">{s.channels_title}</span>
          </div>
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {s.channels.map((c, i) => {
              const Icon = channelIcon(c.label);
              const inner = (
                <>
                  <dt className="flex items-center gap-2 font-mono-utility text-[10px] tracking-[0.25em] uppercase text-[#5b664f] mb-3">
                    <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {c.label}
                  </dt>
                  <dd className="font-serif-display text-xl md:text-2xl text-[#2c1e16] leading-snug">
                    {c.value}
                  </dd>
                </>
              );
              return c.href ? (
                <a
                  key={i}
                  href={c.href}
                  data-testid={`channel-${i}`}
                  className="group block link-underline"
                >
                  {inner}
                </a>
              ) : (
                <div key={i} data-testid={`channel-${i}`} className="block">
                  {inner}
                </div>
              );
            })}
          </dl>
        </div>
      </section>

      {/* FAQ */}
      <section
        data-testid="support-faq-section"
        className="py-24 md:py-32 bg-[#f9f6f0]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-12 gap-6 md:gap-12">
          <div className="col-span-12 md:col-span-4">
            <div className="md:sticky md:top-28">
              <div className="flex items-center gap-3 mb-6">
                <span className="editorial-divider" />
                <span className="overline">{s.faq_overline}</span>
              </div>
              <h2
                data-testid="support-faq-title"
                className="font-serif-display text-4xl md:text-5xl leading-[1.05] tracking-tight text-[#2c1e16]"
              >
                {s.faq_title}
              </h2>
            </div>
          </div>

          <div className="col-span-12 md:col-span-8">
            <Accordion
              type="single"
              collapsible
              className="border-t border-[#e5d9c5]"
              data-testid="support-faq"
            >
              {s.faqs.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="border-b border-[#e5d9c5]"
                >
                  <AccordionTrigger
                    className="py-6 md:py-7 text-left font-serif-display text-xl md:text-2xl text-[#2c1e16] hover:no-underline hover:text-[#a64b2a]"
                    data-testid={`faq-trigger-${i}`}
                  >
                    <span className="pr-6">{f.q}</span>
                  </AccordionTrigger>
                  <AccordionContent
                    className="pb-6 md:pb-8 text-base leading-relaxed text-[#2c1e16]/75"
                    data-testid={`faq-content-${i}`}
                  >
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Contact form */}
      <section
        id="contact-form"
        data-testid="support-form-section"
        className="py-24 md:py-32 bg-[#2c1e16] text-[#f9f6f0] relative overflow-hidden"
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(rgba(249,246,240,0.6) 1px, transparent 1px)",
            backgroundSize: "3px 3px",
          }}
        />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center md:text-left mb-12">
            <div className="flex items-center gap-3 mb-6 justify-center md:justify-start">
              <span className="inline-block w-12 h-px bg-[#a64b2a]" />
              <span className="font-mono-utility text-[10px] tracking-[0.3em] uppercase text-[#a64b2a]">
                {s.form_overline}
              </span>
            </div>
            <h2
              data-testid="support-form-title"
              className="font-serif-display text-4xl md:text-5xl leading-[1.05] tracking-tight mb-5"
            >
              {s.form_title}
            </h2>
            <p className="text-base md:text-lg text-[#f9f6f0]/70 max-w-2xl">
              {s.form_sub}
            </p>
          </div>

          <form
            onSubmit={submit}
            data-testid="support-form"
            className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6"
          >
            <div className="md:col-span-1">
              <label className="block font-mono-utility text-[10px] tracking-[0.25em] uppercase text-[#f9f6f0]/60 mb-2">
                {s.form.name_label}
              </label>
              <input
                data-testid="support-input-name"
                type="text"
                required
                value={form.name}
                onChange={update("name")}
                placeholder={s.form.name_placeholder}
                className="w-full bg-transparent border border-[#f9f6f0]/20 rounded-md px-4 py-3 text-sm text-[#f9f6f0] placeholder:text-[#f9f6f0]/35 focus:outline-none focus:border-[#a64b2a] transition-colors"
              />
            </div>

            <div className="md:col-span-1">
              <label className="block font-mono-utility text-[10px] tracking-[0.25em] uppercase text-[#f9f6f0]/60 mb-2">
                {s.form.email_label}
              </label>
              <input
                data-testid="support-input-email"
                type="email"
                required
                value={form.email}
                onChange={update("email")}
                placeholder={s.form.email_placeholder}
                className="w-full bg-transparent border border-[#f9f6f0]/20 rounded-md px-4 py-3 text-sm text-[#f9f6f0] placeholder:text-[#f9f6f0]/35 focus:outline-none focus:border-[#a64b2a] transition-colors"
              />
            </div>

            <div className="md:col-span-1">
              <label className="block font-mono-utility text-[10px] tracking-[0.25em] uppercase text-[#f9f6f0]/60 mb-2">
                {s.form.topic_label}
              </label>
              <Select
                value={form.topic}
                onValueChange={(v) => setForm((f) => ({ ...f, topic: v }))}
              >
                <SelectTrigger
                  data-testid="support-select-topic"
                  className="w-full bg-transparent border-[#f9f6f0]/20 rounded-md px-4 py-6 text-sm text-[#f9f6f0] focus:ring-0 focus:border-[#a64b2a] data-[placeholder]:text-[#f9f6f0]/35"
                >
                  <SelectValue placeholder={s.form.topic_placeholder} />
                </SelectTrigger>
                <SelectContent className="bg-[#2c1e16] border-[#f9f6f0]/20 text-[#f9f6f0]">
                  {s.form.topics.map((topic) => (
                    <SelectItem
                      key={topic.value}
                      value={topic.value}
                      data-testid={`topic-${topic.value}`}
                      className="focus:bg-[#a64b2a] focus:text-[#f9f6f0] cursor-pointer"
                    >
                      {topic.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-1">
              <label className="block font-mono-utility text-[10px] tracking-[0.25em] uppercase text-[#f9f6f0]/60 mb-2">
                {s.form.ref_label}
              </label>
              <input
                data-testid="support-input-reference"
                type="text"
                value={form.reference}
                onChange={update("reference")}
                placeholder={s.form.ref_placeholder}
                className="w-full bg-transparent border border-[#f9f6f0]/20 rounded-md px-4 py-3 text-sm text-[#f9f6f0] placeholder:text-[#f9f6f0]/35 focus:outline-none focus:border-[#a64b2a] transition-colors"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block font-mono-utility text-[10px] tracking-[0.25em] uppercase text-[#f9f6f0]/60 mb-2">
                {s.form.message_label}
              </label>
              <textarea
                data-testid="support-input-message"
                required
                rows={6}
                value={form.message}
                onChange={update("message")}
                placeholder={s.form.message_placeholder}
                className="w-full bg-transparent border border-[#f9f6f0]/20 rounded-md px-4 py-3 text-sm text-[#f9f6f0] placeholder:text-[#f9f6f0]/35 focus:outline-none focus:border-[#a64b2a] transition-colors resize-none"
              />
            </div>

            <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
              {feedback && (
                <p
                  data-testid="support-form-feedback"
                  className={`text-sm ${
                    status === "success" ? "text-[#d2b48c]" : "text-[#e8a987]"
                  }`}
                >
                  {feedback}
                </p>
              )}
              <button
                data-testid="support-submit-btn"
                type="submit"
                disabled={status === "loading"}
                className="ml-auto inline-flex items-center gap-2 bg-[#a64b2a] hover:bg-[#f9f6f0] hover:text-[#2c1e16] text-[#f9f6f0] px-7 py-4 rounded-full text-xs font-mono-utility tracking-[0.2em] uppercase transition-colors disabled:opacity-50"
              >
                {status === "loading" ? s.form.submitting : s.form.submit}
                {status === "loading" ? null : (
                  <Send className="w-4 h-4" strokeWidth={1.5} />
                )}
              </button>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SupportPage;
